import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { CardNB, ButtonNB, BadgeNB, SelectNB } from '../../../components/ui-nb';
import { useRole } from '../../../hooks/useRole';
import type { Tables } from '@tellowup/db';
import { error as logError } from '../../../lib/logger';

type UserRole = 'technician' | 'cashier' | 'admin';

const ROLE_LABELS: Record<UserRole, string> = {
  technician: 'Technician',
  cashier: 'Cashier',
  admin: 'Admin',
};

// Narrow types for specific Supabase query result shapes
type BranchSummary = Pick<Tables<'branches'>, 'id' | 'name'>;
type BranchUserSummary = Pick<Tables<'branch_user'>, 'branch_id'>;
type OrderSummary = Pick<Tables<'orders'>, 'id' | 'device' | 'device_model' | 'status' | 'created_at'>;

interface BranchOption {
  id: string;
  name: string;
  selected: boolean;
}

interface OrderActivity {
  id: string;
  device: string;
  device_model: string | null;
  status: string;
  created_at: string;
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const role = useRole();
  const [user, setUser] = useState<{
    id: string;
    full_name: string;
    email: string;
    role: UserRole;
    is_active: boolean;
    branches: string[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<UserRole>('technician');
  const [availableBranches, setAvailableBranches] = useState<BranchOption[]>([]);
  const [activity, setActivity] = useState<OrderActivity[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    async function fetchUser() {
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, full_name, email, role, is_active, organization_id')
          .eq('id', id)
          .single();

        if (profileError) throw profileError;
        if (!profileData) throw new Error('User not found');

        // Fetch branches for this user
        const { data: branchUsers } = await supabase
          .from('branch_user')
          .select('branch_id')
          .eq('user_id', id);

        const branchIds = (branchUsers ?? []).map((bu: BranchUserSummary) => bu.branch_id);

        let branchNames: string[] = [];
        if (branchIds.length > 0) {
          const { data: branchesData } = await supabase
            .from('branches')
            .select('name')
            .in('id', branchIds);
          branchNames = (branchesData ?? []).map((b) => b.name);
        }

        // Fetch all branches for this org to build selection list
        const orgId = profileData.organization_id;
        const { data: allBranches } = await supabase
          .from('branches')
          .select('id, name')
          .eq('organization_id', orgId);

        const branchOptions: BranchOption[] = (allBranches ?? []).map((b: BranchSummary) => ({
          id: b.id,
          name: b.name,
          selected: branchIds.includes(b.id),
        }));

        // Fetch activity: last 10 orders assigned to this user
        const { data: ordersData } = await supabase
          .from('orders')
          .select('id, device, device_model, status, created_at')
          .eq('assigned_to', id)
          .order('created_at', { ascending: false })
          .limit(10);

        setUser({
          id: profileData.id,
          full_name: profileData.full_name || '—',
          email: profileData.email || '—',
          role: (profileData.role as UserRole) || 'technician',
          is_active: profileData.is_active ?? true,
          branches: branchNames,
        });
        setEditName(profileData.full_name || '');
        setEditRole((profileData.role as UserRole) || 'technician');
        setAvailableBranches(branchOptions);
        setActivity((ordersData ?? []).map((o: OrderSummary) => ({
          id: o.id,
          device: o.device,
          device_model: o.device_model,
          status: o.status,
          created_at: o.created_at,
        })));
      } catch (err) {
        logError('Error fetching user', { error: err instanceof Error ? err.message : String(err) });
        setError('User not found');
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [id]);

  const handleSave = async () => {
    if (!user || !id) return;
    setSaving(true);
    setSaveError(null);

    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ full_name: editName, role: editRole })
        .eq('id', id);

      if (profileError) throw profileError;

      // Replace all branch_user entries
      const selectedBranchIds = availableBranches.filter(b => b.selected).map(b => b.id);

      // Delete existing
      await supabase.from('branch_user').delete().eq('user_id', id);

      // Insert new
      if (selectedBranchIds.length > 0) {
        const insertRows = selectedBranchIds.map(branchId => ({
          user_id: id,
          branch_id: branchId,
          assigned_at: new Date().toISOString(),
        }));
        const { error: branchError } = await supabase.from('branch_user').insert(insertRows);
        if (branchError) throw branchError;
      }

      // Refresh
      setUser(prev => prev ? {
        ...prev,
        full_name: editName,
        role: editRole,
        branches: availableBranches.filter(b => b.selected).map(b => b.name),
      } : null);
      setEditing(false);
    } catch (err) {
      logError('Error saving user', { error: err instanceof Error ? err.message : String(err) });
      setSaveError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const toggleBranch = (branchId: string) => {
    setAvailableBranches(prev =>
      prev.map(b => b.id === branchId ? { ...b, selected: !b.selected } : b)
    );
  };

  if (loading) {
    return <p className="text-nb-text-muted">Loading...</p>;
  }

  if (error || !user) {
    return <p className="text-nb-red">{error || 'User not found'}</p>;
  }

  return (
    <>
      <div className="mb-6">
        <Link to="/en/admin/users" className="text-nb-black font-nb font-medium">
          ← Back to Users
        </Link>
      </div>

      {saveError && (
        <div className="bg-nb-red/10 text-nb-red p-3 rounded mb-4 text-sm">
          {saveError}
        </div>
      )}

      <CardNB className="p-0 overflow-hidden">
        <div className="p-4 border-b-3 border-nb-black flex items-center justify-between">
          <h1 className="text-lg font-bold text-nb-black">User Details</h1>
          <div className="flex gap-2">
            {editing ? (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="text-nb-text-muted text-sm font-nb font-medium bg-none border-none cursor-pointer"
                >
                  Cancel
                </button>
                <ButtonNB
                  variant="primary"
                  size="sm"
                  loading={saving}
                  onClick={handleSave}
                >
                  Save
                </ButtonNB>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="text-nb-black text-sm font-nb font-medium bg-none border-none cursor-pointer"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="p-6 flex flex-col gap-5">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-nb-black text-nb-white flex items-center justify-center font-nb font-bold text-xl">
              {user.full_name.charAt(0)}
            </div>
            <div>
              {editing ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="font-nb w-full p-2 border-2 border-nb-black bg-nb-white text-nb-black text-sm outline-none"
                  style={{ borderRadius: 0 }}
                />
              ) : (
                <div className="text-lg font-nb font-semibold text-nb-black">{user.full_name}</div>
              )}
              <div className="text-sm text-nb-text-muted">{user.email}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              {editing && role === 'admin' ? (
                <SelectNB
                  label="Role"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as UserRole)}
                  className="w-full"
                >
                  <option value="technician">Technician</option>
                  <option value="cashier">Cashier</option>
                  <option value="admin">Admin</option>
                </SelectNB>
              ) : (
                <span className="text-sm text-nb-black">{ROLE_LABELS[user.role]}</span>
              )}
            </div>
            <div>
              <label className="block text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide mb-1">Status</label>
              <BadgeNB variant={user.is_active ? 'success' : 'warning'}>
                {user.is_active ? 'Active' : 'Inactive'}
              </BadgeNB>
            </div>
          </div>

          <div>
            <label className="block text-xs font-nb font-medium text-nb-text-muted uppercase tracking-wide mb-2">
              Assigned branches
            </label>
            {editing ? (
              <div className="flex flex-wrap gap-2">
                {availableBranches.map((branch) => (
                  <button
                    key={branch.id}
                    onClick={() => toggleBranch(branch.id)}
                    className={`text-sm px-3 py-1 border-2 border-nb-black cursor-pointer transition-colors ${
                      branch.selected
                        ? 'bg-nb-black text-nb-white'
                        : 'bg-nb-white text-nb-black'
                    }`}
                    style={{ borderRadius: 0 }}
                  >
                    {branch.name}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {user.branches.map((branch) => (
                  <span key={branch} className="text-sm bg-nb-cream text-nb-text-secondary px-3 py-1">
                    {branch}
                  </span>
                ))}
                {user.branches.length === 0 && (
                  <span className="text-sm text-nb-text-muted">No branches assigned</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardNB>

      {/* Activity History */}
      <CardNB className="p-0 overflow-hidden mt-6">
        <div className="p-4 border-b-3 border-nb-black">
          <h2 className="text-lg font-bold text-nb-black">Activity History</h2>
        </div>
        <div className="p-4">
          {activity.length === 0 ? (
            <p className="text-sm text-nb-text-muted">No activity yet</p>
          ) : (
            <div className="flex flex-col gap-2">
              {activity.map((order) => (
                <div key={order.id} className="flex items-center justify-between py-2 border-b border-nb-black/10">
                  <div>
                    <div className="text-sm font-nb font-medium text-nb-black">
                      {order.device} {order.device_model ? `(${order.device_model})` : ''}
                    </div>
                    <div className="text-xs text-nb-text-muted">
                      Order #{order.id.slice(0, 8)} · {new Date(order.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <BadgeNB variant="info" className="text-xs">
                    {order.status}
                  </BadgeNB>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardNB>
    </>
  );
}